import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Temporadas.css';
import { getTemporadaReal, actualizarTemporada, borrarTemporada } from '../../services/temporadasService';
import { getParticipantesReal } from '../../services/participantesService';
import { getCalendarioDetalle } from '../../services/jornadasService';
import ParticipantesTab from './ParticipantesTab';
import ClubesTab from './ClubesTab';
import RoundRobinTab from './RoundRobinTab';
import DescargaTab from './DescargaTab';
import LicenciasTab from './LicenciasTab';

const VISTA_KEY = 'temporadas.vistaParticipantes';
const leerVista = () => {
  try { return localStorage.getItem(VISTA_KEY) === 'club' ? 'club' : 'modalidad'; } catch { return 'modalidad'; }
};

const MAX_JUG = { 'DOBLES MIXTO': 4, default: 3 };

// No hay titulares/suplentes en la base de datos ("no hay reservas: todos
// juegan y se turnan"): la única composición que se valida aquí es cuántos
// hay y de qué género, no quién sale a pista cada partido — eso lo exige el
// backend al registrar la alineación de cada partido.
function validar(participantes, jornadas) {
  const errs = [];

  if (!jornadas.length)
    errs.push('No hay jornadas generadas. Ejecuta el cálculo de Round Robin primero.');

  const modalidades = [...new Set(participantes.map(p => p.modalidad))];
  modalidades.forEach(mod => {
    const activos = participantes.filter(p => p.modalidad === mod && p.estado !== 'DESCALIFICADO');
    if (activos.length < 2)
      errs.push(`${mod}: se necesitan al menos 2 participantes (hay ${activos.length}).`);

    if (mod.includes('DOBLES')) {
      activos.forEach(p => {
        const max = MAX_JUG[mod] ?? MAX_JUG.default;
        if (p.jugadores.length > max)
          errs.push(`${mod} — "${p.nombre_mostrar}": máximo ${max} jugadores (tiene ${p.jugadores.length}).`);
        if (p.jugadores.length < 2)
          errs.push(`${mod} — "${p.nombre_mostrar}": mínimo 2 jugadores.`);
        if (mod === 'DOBLES MIXTO') {
          const h = p.jugadores.filter(j => j.genero === 'M').length;
          const m = p.jugadores.filter(j => j.genero === 'F').length;
          if (h < 1 || h > 2 || m < 1 || m > 2)
            errs.push(`Dobles Mixto — "${p.nombre_mostrar}": debe haber entre 1 y 2 hombres y entre 1 y 2 mujeres.`);
        }
      });

      const conteo = {};
      activos.forEach(p => p.jugadores.forEach(j => {
        conteo[j.jugadorId] = conteo[j.jugadorId] || [];
        conteo[j.jugadorId].push(p.nombre_mostrar);
      }));
      Object.entries(conteo).forEach(([, equipos]) => {
        if (equipos.length > 1)
          errs.push(`${mod}: jugador aparece en más de un equipo (${equipos.join(', ')}).`);
      });
    }
  });

  return errs;
}

export default function TemporadaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [temporada, setTemporada] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [tab, setTab] = useState('participantes');
  const [vista, setVista] = useState(leerVista);
  const [clubesPendientes, setClubesPendientes] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [editNombre, setEditNombre] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    const [t, p, j] = await Promise.all([
      getTemporadaReal(id),
      getParticipantesReal(id),
      getCalendarioDetalle(id),
    ]);
    setTemporada(t);
    setParticipantes(p);
    setJornadas(j);
    setNuevoNombre(t.nombre);
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (nuevoEstado) => {
    if (nuevoEstado === 'INICIADA') {
      const errs = validar(participantes, jornadas);
      if (errs.length) { setErroresValidacion(errs); return; }
    }
    setErroresValidacion([]);
    setError(null);
    try {
      await actualizarTemporada(id, { estado: nuevoEstado });
      cargar();
    } catch (err) {
      setError(err.message || 'Error al cambiar el estado.');
    }
  };

  const guardarNombre = async () => {
    if (!nuevoNombre.trim()) return;
    await actualizarTemporada(id, { nombre: nuevoNombre.trim() });
    setEditNombre(false); cargar();
  };

  const borrar = async () => {
    if (!window.confirm(`¿Borrar temporada "${temporada.nombre}"? Esta acción no se puede deshacer.`)) return;
    await borrarTemporada(id);
    navigate('/privado/temporadas');
  };

  // Los cambios sin confirmar de la vista "Por club" viven en el estado de
  // ClubesTab: salir de ella (otra pestaña u otra vista) los perdería.
  const confirmarSalida = () =>
    !clubesPendientes || window.confirm('Tienes cambios sin confirmar en los clubes. Si sales los perderás. ¿Salir?');

  const cambiarTab = (nueva) => {
    if (nueva === tab || !confirmarSalida()) return;
    setClubesPendientes(false);
    setTab(nueva);
  };

  const cambiarVista = (nueva) => {
    if (nueva === vista || !confirmarSalida()) return;
    setClubesPendientes(false);
    setVista(nueva);
    try { localStorage.setItem(VISTA_KEY, nueva); } catch { /* sin almacenamiento: no pasa nada */ }
  };

  if (!temporada) return <div className="page"><p>Cargando...</p></div>;

  const esPlanificada = temporada.estado === 'PLANIFICADA';
  const esIniciada = temporada.estado === 'INICIADA';
  const esFinalizada = temporada.estado === 'FINALIZADA';
  const soloLectura = esFinalizada;

  // Sin round robin generado en alguna modalidad (o en ninguna) no se puede arrancar.
  const modalidadesSinCalendario = [...new Set(participantes.map(p => p.modalidad))]
    .filter(mod => !jornadas.some(j => j.modalidad === mod));
  const faltaCalendario = !jornadas.length || modalidadesSinCalendario.length > 0;
  const motivoSinCalendario = !jornadas.length
    ? 'No hay partidos de round robin programados.'
    : `Faltan partidos de round robin en: ${modalidadesSinCalendario.join(', ')}.`;

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => confirmarSalida() && navigate('/privado/temporadas')}>← Temporadas</button>

        {editNombre && esPlanificada ? (
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flex: 1 }}>
            <input
              value={nuevoNombre}
              onChange={e => setNuevoNombre(e.target.value)}
              style={{ fontSize: '1.2rem', fontWeight: 700, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 6 }}
            />
            <button className="btn btn-primary" onClick={guardarNombre}>Guardar</button>
            <button className="btn btn-secondary" onClick={() => setEditNombre(false)}>Cancelar</button>
          </div>
        ) : (
          <h2 style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '.6rem' }}>
            {temporada.nombre}
            {esPlanificada && (
              <button className="btn btn-secondary" style={{ fontSize: '.75rem' }} onClick={() => setEditNombre(true)}>
                ✎ Editar
              </button>
            )}
            <span className={`badge-estado badge-${temporada.estado}`}>{temporada.estado}</span>
          </h2>
        )}

        <div className="detalle-actions">
          {esPlanificada && (
            <>
              <button
                className="btn btn-success"
                onClick={() => cambiarEstado('INICIADA')}
                disabled={faltaCalendario}
                title={faltaCalendario ? motivoSinCalendario : undefined}
              >
                ▶ Iniciar temporada
              </button>
              <button className="btn btn-danger" onClick={borrar}>Borrar</button>
            </>
          )}
          {esIniciada && (
            <button className="btn btn-warning" onClick={() => cambiarEstado('FINALIZADA')}>
              ■ Finalizar temporada
            </button>
          )}
          {esFinalizada && (
            <span style={{ fontSize: '.85rem', color: '#888' }}>Temporada cerrada</span>
          )}
        </div>
      </div>

      {error && <p className="error-msg">{error}</p>}

      {erroresValidacion.length > 0 && (
        <div className="validaciones-panel">
          <h4>⚠ No se puede iniciar — revisa lo siguiente:</h4>
          <ul>{erroresValidacion.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      <div className="tabs">
        {[
          { key: 'participantes', label: `Participantes (${participantes.length})` },
          { key: 'jornadas', label: `Jornadas (${jornadas.length})` },
          { key: 'licencias', label: 'Licencias y correos' },
          { key: 'descarga', label: 'Descargar' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-btn${tab === t.key ? ' active' : ''}`}
            onClick={() => cambiarTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'participantes' && (
        <div className="vista-toggle" role="group" aria-label="Vista de participantes">
          <button className={vista === 'modalidad' ? 'active' : ''} onClick={() => cambiarVista('modalidad')}>Por modalidad</button>
          <button className={vista === 'club' ? 'active' : ''} onClick={() => cambiarVista('club')}>Por club</button>
        </div>
      )}
      {tab === 'participantes' && vista === 'modalidad' && (
        <ParticipantesTab
          temporada={id}
          participantes={participantes}
          onRefresh={cargar}
          soloLectura={soloLectura}
        />
      )}
      {tab === 'participantes' && vista === 'club' && (
        <ClubesTab
          temporada={id}
          estado={temporada.estado}
          participantes={participantes}
          onCambiosPendientes={setClubesPendientes}
        />
      )}
      {tab === 'jornadas' && (
        <RoundRobinTab
          temporada={id}
          jornadas={jornadas}
          participantes={participantes}
          onRefresh={cargar}
          soloLectura={soloLectura || esIniciada}
        />
      )}
      {tab === 'licencias' && <LicenciasTab temporada={id} estado={temporada.estado} />}
      {tab === 'descarga' && (
        <DescargaTab temporada={id} jornadas={jornadas} participantes={participantes} onRefresh={cargar} editable={esIniciada} />
      )}
    </div>
  );
}
